import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, MapPin, Users, Clock, Calendar, CreditCard, Package,
  CheckCircle2, Sparkles, Loader2, Plus, X, Tag,
  ArrowLeft, ShoppingCart, DollarSign, Minus, Info, Filter,
  ArrowUpDown, Wrench, Truck, Star, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ['Corporate Event', 'Wedding', 'Birthday Party', 'Holiday Party', 'Conference', 'Workshop', 'Networking', 'Product Launch', 'Private Dinner', 'Other'];

const PRICING_MODEL_LABELS = {
  flat_rate: 'Flat Rate', per_hour: 'Per Hour', per_person: 'Per Person',
  per_hour_per_person: 'Per Hour/Person'
};

export default function BundleCheckoutPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const venueId = params.get('venue_id');
  const venueSlug = params.get('slug');
  const initialPackageId = params.get('package_id');

  const [venueData, setVenueData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Multi-space selections: [{space_id, start_time, end_time}]
  const [spaceSelections, setSpaceSelections] = useState(() => {
    const sid = params.get('space_id');
    if (sid) return [{ space_id: sid, start_time: params.get('start') || '10:00', end_time: params.get('end') || '18:00' }];
    return [];
  });

  const [selectedPackageId, setSelectedPackageId] = useState(initialPackageId || '');
  const [extraServiceIds, setExtraServiceIds] = useState(() => {
    const ids = params.get('service_ids');
    return ids ? ids.split(',').filter(Boolean) : [];
  });
  const [addonSelections, setAddonSelections] = useState(() => {
    const ids = params.get('addon_ids');
    return ids ? ids.split(',').filter(Boolean).map(id => ({ addon_id: id, quantity: 1 })) : [];
  });

  // Per-service customizations: { service_id: { selectedOptions, guestCount, hours, isDropoff } }
  const [serviceCustomizations, setServiceCustomizations] = useState({});

  // Service sorting & filtering
  const [svcSort, setSvcSort] = useState('name');
  const [svcCatFilter, setSvcCatFilter] = useState('');

  // Full service detail modal
  const [detailSvc, setDetailSvc] = useState(null);
  const [detailSvcFull, setDetailSvcFull] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedServiceType, setSelectedServiceType] = useState('serviced');
  const [detailGalleryIdx, setDetailGalleryIdx] = useState(0);

  const [form, setForm] = useState({
    event_type: params.get('event_type') || 'Corporate Event',
    event_date: params.get('date') || '',
    guest_count: parseInt(params.get('guests')) || 50,
    setup_time: '30',
    breakdown_time: '30',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load venue data
  useEffect(() => {
    if (!venueSlug && !venueId) return;
    const load = async () => {
      try {
        const identifier = venueSlug || venueId;
        const venueRes = await axios.get(`${API}/venues/detail/${identifier}`);
        setVenueData(venueRes.data);
        if (spaceSelections.length === 0 && venueRes.data.spaces?.[0]) {
          setSpaceSelections([{ space_id: venueRes.data.spaces[0].space_id, start_time: '10:00', end_time: '18:00' }]);
        }
        if (user) { set('customer_name', user.name || ''); set('customer_email', user.email || ''); }
      } catch (err) { console.error('Failed to load venue:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [venueSlug, venueId]);

  // Fetch pricing preview
  const fetchPreview = useCallback(async () => {
    const actualVenueId = venueData?.venue?.venue_id || venueId;
    if (!actualVenueId || spaceSelections.length === 0) return;
    try {
      const payload = {
        venue_id: actualVenueId,
        package_id: selectedPackageId || null,
        guest_count: form.guest_count,
        extra_service_ids: extraServiceIds,
        addon_selections: addonSelections,
      };
      if (selectedPackageId) {
        payload.space_id = spaceSelections[0]?.space_id;
        payload.start_time = spaceSelections[0]?.start_time || '10:00';
        payload.end_time = spaceSelections[0]?.end_time || '18:00';
      } else if (spaceSelections.length === 1) {
        payload.space_id = spaceSelections[0].space_id;
        payload.start_time = spaceSelections[0].start_time;
        payload.end_time = spaceSelections[0].end_time;
      } else {
        payload.space_selections = spaceSelections;
      }
      const res = await axios.post(`${API}/cart/bundle-preview`, payload);
      setPreview(res.data);
    } catch (err) { console.error('Preview error:', err); }
  }, [venueData, venueId, spaceSelections, selectedPackageId, form.guest_count, extraServiceIds, addonSelections]);

  useEffect(() => { if (venueData) fetchPreview(); }, [fetchPreview]);

  // Space selection helpers
  const isSpaceSelected = (spaceId) => spaceSelections.some(s => s.space_id === spaceId);

  const toggleSpace = (spaceId) => {
    if (selectedPackageId) setSelectedPackageId('');
    setSpaceSelections(prev => {
      if (prev.some(s => s.space_id === spaceId)) {
        const filtered = prev.filter(s => s.space_id !== spaceId);
        return filtered.length > 0 ? filtered : prev; // keep at least one
      }
      return [...prev, { space_id: spaceId, start_time: '10:00', end_time: '18:00' }];
    });
  };

  const updateSpaceTime = (spaceId, field, value) => {
    setSpaceSelections(prev => prev.map(s =>
      s.space_id === spaceId ? { ...s, [field]: value } : s
    ));
  };

  const toggleExtraService = (svcId) => {
    setExtraServiceIds(p => p.includes(svcId) ? p.filter(s => s !== svcId) : [...p, svcId]);
  };

  const toggleAddon = (addonId) => {
    setAddonSelections(prev => {
      const exists = prev.find(a => a.addon_id === addonId);
      if (exists) return prev.filter(a => a.addon_id !== addonId);
      return [...prev, { addon_id: addonId, quantity: 1 }];
    });
  };

  const updateAddonQty = (addonId, delta) => {
    setAddonSelections(prev => prev.map(a => {
      if (a.addon_id !== addonId) return a;
      return { ...a, quantity: Math.max(1, a.quantity + delta) };
    }));
  };

  // Open full service detail modal
  const openServiceDetail = async (svc) => {
    setDetailSvc(svc);
    setDetailSvcFull(null);
    setDetailTab('overview');
    setSelectedOptions({});
    setSelectedServiceType('serviced');
    setDetailGalleryIdx(0);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/services/products/${svc.service_id}`);
      const full = res.data;
      setDetailSvcFull(full);
      // Set default options
      const defaults = {};
      full.option_groups?.forEach(og => {
        const defChoices = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
        if (defChoices.length > 0) {
          defaults[og.option_id] = og.multiple_select ? defChoices : defChoices[0];
        }
      });
      setSelectedOptions(defaults);
      // Restore customizations if already added
      const existing = serviceCustomizations[svc.service_id];
      if (existing?.selectedOptions) setSelectedOptions(existing.selectedOptions);
      if (existing?.isDropoff) setSelectedServiceType('dropoff');
    } catch (err) { console.error('Failed to load full service:', err); }
    finally { setDetailLoading(false); }
  };

  const handleOptionChange = (optionId, choiceId, isMultiple) => {
    setSelectedOptions(prev => {
      if (isMultiple) {
        const current = prev[optionId] || [];
        return { ...prev, [optionId]: current.includes(choiceId) ? current.filter(id => id !== choiceId) : [...current, choiceId] };
      }
      return { ...prev, [optionId]: choiceId };
    });
  };

  const addServiceFromModal = () => {
    if (!detailSvc) return;
    const svcId = detailSvc.service_id;
    const isDropoff = selectedServiceType === 'dropoff' && detailSvcFull?.has_dropoff_option;
    const optionsForCart = {};
    if (!isDropoff && detailSvcFull?.option_groups?.length > 0) {
      Object.entries(selectedOptions).forEach(([optId, value]) => {
        optionsForCart[optId] = Array.isArray(value) ? value : [value];
      });
    }
    setServiceCustomizations(prev => ({
      ...prev,
      [svcId]: { selectedOptions: optionsForCart, isDropoff, guestCount: form.guest_count }
    }));
    if (!extraServiceIds.includes(svcId)) {
      setExtraServiceIds(prev => [...prev, svcId]);
    }
    setDetailSvc(null);
    setDetailSvcFull(null);
    toast.success(`${detailSvc.name} added to booking${isDropoff ? ' (Drop-off)' : ''}`);
  };

  const handleCheckout = async () => {
    if (!form.event_date || !form.customer_name || !form.customer_email) {
      toast.error('Date, name, and email are required'); return;
    }
    if (!token) { toast.error('Please sign in to checkout'); return; }
    setSubmitting(true);
    try {
      const pkg = venueData?.packages?.find(p => p.package_id === selectedPackageId);
      const actualVenueId = venueData?.venue?.venue_id || venueId;
      const pkgAddonSels = pkg?.addons?.map(a => ({ addon_id: a.addon_id, quantity: a.quantity })) || [];
      const primarySpace = spaceSelections[0];

      const res = await axios.post(`${API}/cart/bundle-checkout`, {
        venue_id: actualVenueId,
        space_id: primarySpace?.space_id,
        package_id: selectedPackageId || null,
        event_type: form.event_type,
        event_date: form.event_date,
        start_time: primarySpace?.start_time || '10:00',
        end_time: primarySpace?.end_time || '18:00',
        guest_count: form.guest_count,
        setup_time: form.setup_time ? `${form.setup_time} min` : null,
        breakdown_time: form.breakdown_time ? `${form.breakdown_time} min` : null,
        addon_selections: selectedPackageId ? pkgAddonSels : addonSelections,
        service_ids: pkg?.services?.map(s => s.service_id) || [],
        extra_service_ids: extraServiceIds,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes + (spaceSelections.length > 1 ? `\n[Multi-space booking: ${spaceSelections.map(s => `${s.space_id} ${s.start_time}-${s.end_time}`).join(', ')}]` : ''),
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success('Booking created! The venue owner will confirm shortly.');
        navigate(`/venues/${venueData?.venue?.slug || ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally { setSubmitting(false); }
  };

  // Derived data (before any early return)
  const venue = venueData?.venue;
  const spaces = venueData?.spaces || [];
  const allAddons = venueData?.addons || [];
  const allPackages = venueData?.packages || [];
  const preferredServices = venueData?.preferred_services || [];
  const pricing = preview?.pricing;
  const spacesBreakdown = preview?.spaces_breakdown;

  const pkg = allPackages.find(p => p.package_id === selectedPackageId);
  const pkgSvcIds = pkg?.services?.map(s => s.service_id) || [];
  const availableExtras = preferredServices.filter(s => !pkgSvcIds.includes(s.service_id));

  const svcCategories = [...new Set(availableExtras.map(s => s.category).filter(Boolean))];
  const sortedExtras = useMemo(() => {
    let list = svcCatFilter ? availableExtras.filter(s => s.category === svcCatFilter) : [...availableExtras];
    if (svcSort === 'price_asc') list.sort((a, b) => (a.base_price || 0) - (b.base_price || 0));
    else if (svcSort === 'price_desc') list.sort((a, b) => (b.base_price || 0) - (a.base_price || 0));
    else if (svcSort === 'category') list.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    else list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [availableExtras, svcSort, svcCatFilter]);

  const pkgAddonIds = pkg?.addons?.map(a => a.addon_id) || [];
  const availableAddons = selectedPackageId ? allAddons.filter(a => !pkgAddonIds.includes(a.addon_id)) : allAddons;

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;
  if (!venue) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Venue not found</p></div>;

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="bundle-checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(venueSlug ? `/venues/${venueSlug}` : '/venues')} className="text-xs text-muted-foreground mb-4" data-testid="back-btn">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Venue
        </Button>

        <div className="flex items-center gap-2 mb-6">
          <ShoppingCart className="h-5 w-5 text-indigo-400" />
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Bundle Checkout</h1>
        </div>

        <div className="lg:flex lg:gap-6">
          {/* Left Column */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* Venue Header */}
            <Card className="bg-muted/60 border-border" data-testid="cart-venue-section">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {venue.cover_photo && <img src={venue.cover_photo} alt="" className="w-24 h-20 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1">
                    <Badge className="bg-indigo-500/20 text-indigo-400 text-[8px] mb-1">Venue Rental Partner</Badge>
                    <h3 className="text-base font-bold text-foreground">{venue.name}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{venue.address}, {venue.city}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-foreground">${pricing?.space_cost?.toLocaleString() || '...'}</p>
                    <p className="text-[10px] text-muted-foreground">{spaceSelections.length} space{spaceSelections.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Multi-Space Selection */}
            <Card className="bg-muted/60 border-border" data-testid="space-selection-section">
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-indigo-400" /> Select Spaces
                </h3>
                <p className="text-[9px] text-muted-foreground mb-3">Choose one or more spaces. Each gets its own time slot.</p>
                <div className="space-y-2">
                  {spaces.map(s => {
                    const selected = isSpaceSelected(s.space_id);
                    const sel = spaceSelections.find(sp => sp.space_id === s.space_id);
                    return (
                      <div key={s.space_id}
                        className={`rounded-lg border transition-all ${selected ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-muted/50 border-border hover:border-border'}`}
                        data-testid={`space-option-${s.space_id}`}>
                        <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => toggleSpace(s.space_id)}>
                          <Checkbox checked={selected} onCheckedChange={() => toggleSpace(s.space_id)} className="data-[state=checked]:bg-indigo-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{s.name}</p>
                            <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                              <span><Users className="h-2.5 w-2.5 inline mr-0.5" />{s.capacity_min}-{s.capacity_max}</span>
                              {s.minimum_hours > 0 && <span><Clock className="h-2.5 w-2.5 inline mr-0.5" />{s.minimum_hours}hr min</span>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {s.hourly_rate > 0 && <p className="text-sm font-bold text-foreground">${s.hourly_rate}<span className="text-[10px] text-muted-foreground">/hr</span></p>}
                            {s.full_day_rate > 0 && <p className="text-[10px] text-muted-foreground">${s.full_day_rate} full-day</p>}
                          </div>
                        </div>
                        {/* Per-space time slot */}
                        {selected && (
                          <div className="px-3 pb-3 pt-1 border-t border-border/50">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <div className="flex items-center gap-1.5">
                                <Input type="time" value={sel?.start_time || '10:00'}
                                  onChange={e => updateSpaceTime(s.space_id, 'start_time', e.target.value)}
                                  className="bg-muted border-border h-7 text-[11px] w-24"
                                  data-testid={`space-start-${s.space_id}`} />
                                <span className="text-[10px] text-muted-foreground">to</span>
                                <Input type="time" value={sel?.end_time || '18:00'}
                                  onChange={e => updateSpaceTime(s.space_id, 'end_time', e.target.value)}
                                  className="bg-muted border-border h-7 text-[11px] w-24"
                                  data-testid={`space-end-${s.space_id}`} />
                              </div>
                              {spacesBreakdown && spacesBreakdown.find(sb => sb.space_id === s.space_id) && (
                                <span className="text-[10px] text-emerald-400 font-medium ml-auto">
                                  ${spacesBreakdown.find(sb => sb.space_id === s.space_id).cost.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Package info */}
            {pkg && (
              <Card className="bg-indigo-500/5 border-indigo-500/20" data-testid="cart-package-section">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-400" />
                      <h3 className="text-sm font-bold text-foreground">{pkg.name}</h3>
                      {pkg.savings > 0 && <Badge className="bg-lime-500/20 text-lime-400 text-[8px]">Save ${pkg.savings}</Badge>}
                    </div>
                    <button onClick={() => setSelectedPackageId('')} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-0.5" data-testid="remove-package-btn">
                      <X className="h-3 w-3" /> Remove
                    </button>
                  </div>
                  {pkg.addons?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] text-muted-foreground uppercase mb-1">Included Add-ons</p>
                      <div className="flex flex-wrap gap-1">{pkg.addons.map(a => <Badge key={a.addon_id} className="text-[8px] bg-muted text-muted-foreground">{a.name} {a.quantity > 1 ? `x${a.quantity}` : ''}</Badge>)}</div>
                    </div>
                  )}
                  {pkg.services?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase mb-1">Included Services</p>
                      <div className="flex flex-wrap gap-1">{pkg.services.map(s => <Badge key={s.service_id} className="text-[8px] bg-indigo-500/15 text-indigo-300">{s.name}</Badge>)}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Available Add-ons */}
            {availableAddons.length > 0 && (
              <Card className="bg-muted/60 border-border" data-testid="available-addons-section">
                <CardContent className="p-4">
                  <h3 className="text-xs font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-emerald-400" /> Available Add-ons
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {availableAddons.map(addon => {
                      const sel = addonSelections.find(a => a.addon_id === addon.addon_id);
                      const isAdded = !!sel;
                      return (
                        <div key={addon.addon_id}
                          className={`p-3 rounded-lg border transition-all ${isAdded ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-muted/50 border-border'}`}
                          data-testid={`addon-option-${addon.addon_id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground">{addon.name}</p>
                              {addon.description && <p className="text-[9px] text-muted-foreground mt-0.5 line-clamp-1">{addon.description}</p>}
                              <p className="text-[10px] text-emerald-400 font-semibold mt-1">
                                ${addon.price} {addon.pricing_type === 'per_unit' ? '/unit' : addon.pricing_type === 'per_hour' ? '/hr' : 'flat'}
                              </p>
                            </div>
                            {!isAdded ? (
                              <Button size="sm" variant="outline" className="h-7 text-[10px] border-border text-muted-foreground ml-2" onClick={() => toggleAddon(addon.addon_id)}>
                                <Plus className="h-3 w-3 mr-0.5" /> Add
                              </Button>
                            ) : (
                              <div className="flex items-center gap-1 ml-2">
                                {addon.pricing_type === 'per_unit' && (
                                  <>
                                    <button onClick={() => updateAddonQty(addon.addon_id, -1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center text-muted-foreground hover:bg-accent"><Minus className="h-3 w-3" /></button>
                                    <span className="text-xs text-foreground w-6 text-center font-medium">{sel.quantity}</span>
                                    <button onClick={() => updateAddonQty(addon.addon_id, 1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center text-muted-foreground hover:bg-accent"><Plus className="h-3 w-3" /></button>
                                  </>
                                )}
                                <button onClick={() => toggleAddon(addon.addon_id)} className="text-red-400 hover:text-red-300 p-1 ml-1" data-testid={`remove-addon-${addon.addon_id}`}><X className="h-3 w-3" /></button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Included Services from preview */}
            {pricing?.services?.filter(s => s.from_package).length > 0 && (
              <Card className="bg-muted/60 border-border" data-testid="cart-included-services">
                <CardContent className="p-4">
                  <h3 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-indigo-400" /> Included Services</h3>
                  <div className="space-y-2">
                    {pricing.services.filter(s => s.from_package).map(svc => (
                      <div key={svc.service_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                        {svc.image_url && <img src={svc.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground">{svc.name}</p>
                          <p className="text-[9px] text-muted-foreground capitalize">{svc.category} | {svc.pricing_model?.replace('_', ' ')}</p>
                        </div>
                        <p className="text-sm font-semibold text-foreground">${svc.calculated_price?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Extra Services added - EDITABLE */}
            {pricing?.services?.filter(s => !s.from_package).length > 0 && (
              <Card className="bg-muted/60 border-border" data-testid="cart-extra-services">
                <CardContent className="p-4">
                  <h3 className="text-xs font-semibold text-foreground mb-2">Additional Services</h3>
                  <div className="space-y-2">
                    {pricing.services.filter(s => !s.from_package).map(svc => {
                      const cust = serviceCustomizations[svc.service_id];
                      const basicSvc = preferredServices.find(ps => ps.service_id === svc.service_id) || svc;
                      return (
                        <div key={svc.service_id}
                          className="flex items-center gap-3 p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors group"
                          onClick={() => openServiceDetail(basicSvc)}
                          data-testid={`edit-service-${svc.service_id}`}>
                          {svc.image_url && <img src={svc.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground">{svc.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-[9px] text-muted-foreground capitalize">{svc.category}</p>
                              {cust?.isDropoff && <Badge className="bg-emerald-500/20 text-emerald-400 text-[7px]">Drop-off</Badge>}
                              {cust?.selectedOptions && Object.keys(cust.selectedOptions).length > 0 && <Badge className="bg-indigo-500/20 text-indigo-300 text-[7px]">Customized</Badge>}
                            </div>
                          </div>
                          <span className="text-[9px] text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">Edit</span>
                          <p className="text-sm font-semibold text-foreground">${svc.calculated_price?.toLocaleString()}</p>
                          <button onClick={(e) => { e.stopPropagation(); toggleExtraService(svc.service_id); }} className="text-red-400 hover:text-red-300 p-1"><X className="h-3 w-3" /></button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add More Services */}
            {sortedExtras.length > 0 && (
              <Card className="bg-muted/60 border-border border-dashed" data-testid="add-more-services">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5"><Plus className="h-3.5 w-3.5 text-emerald-400" /> Add Venue-Approved Services</h3>
                  </div>
                  <p className="text-[9px] text-muted-foreground mb-3">Click a service to customize options, or quick-add with the + button.</p>

                  {/* Sort & Filter */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="service-sort-filter">
                    {svcCategories.length > 1 && (
                      <div className="flex items-center gap-1">
                        <Filter className="h-3 w-3 text-muted-foreground" />
                        <select value={svcCatFilter} onChange={e => setSvcCatFilter(e.target.value)}
                          className="bg-muted border border-border rounded text-[10px] text-muted-foreground px-2 py-1 focus:outline-none focus:border-indigo-500" data-testid="service-category-select">
                          <option value="">All Categories</option>
                          {svcCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                      <select value={svcSort} onChange={e => setSvcSort(e.target.value)}
                        className="bg-muted border border-border rounded text-[10px] text-muted-foreground px-2 py-1 focus:outline-none focus:border-indigo-500" data-testid="service-sort-select">
                        <option value="name">Name A-Z</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                        <option value="category">Category</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {sortedExtras.map(svc => {
                      const isAdded = extraServiceIds.includes(svc.service_id);
                      return (
                        <div key={svc.service_id}
                          className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-left transition-all cursor-pointer ${isAdded ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-muted/50 border-border hover:border-border'}`}
                          onClick={() => openServiceDetail(svc)}
                          data-testid={`add-service-${svc.service_id}`}>
                          {svc.image_url ? <img src={svc.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" /> :
                            <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-foreground truncate">{svc.name}</p>
                            <p className="text-[9px] text-muted-foreground capitalize">{svc.category} | {PRICING_MODEL_LABELS[svc.pricing_model] || svc.pricing_model}</p>
                            <p className="text-[9px] text-emerald-400">From ${svc.base_price}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); toggleExtraService(svc.service_id); }}
                              className={`p-1.5 rounded transition-colors ${isAdded ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-muted-foreground hover:bg-accent hover:text-emerald-400'}`}
                              data-testid={`toggle-service-${svc.service_id}`}>
                              {isAdded ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Event Details */}
            <Card className="bg-muted/60 border-border" data-testid="event-details-section">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-xs font-semibold text-foreground">Event Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px]">Event Type</Label>
                    <Select value={form.event_type} onValueChange={v => set('event_type', v)}>
                      <SelectTrigger className="bg-muted border-border h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Event Date *</Label>
                    <Input type="date" value={form.event_date} onChange={e => set('event_date', e.target.value)} className="bg-muted border-border h-8 text-xs" data-testid="checkout-date" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px]">Guests</Label><Input type="number" value={form.guest_count} onChange={e => set('guest_count', parseInt(e.target.value) || 1)} className="bg-muted border-border h-8 text-xs" data-testid="checkout-guests" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] flex items-center gap-1"><Wrench className="h-2.5 w-2.5 text-muted-foreground" /> Setup</Label>
                      <Select value={form.setup_time} onValueChange={v => set('setup_time', v)}>
                        <SelectTrigger className="bg-muted border-border h-8 text-xs" data-testid="setup-time-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{['0','15','30','45','60','90','120'].map(m => <SelectItem key={m} value={m}>{m === '0' ? 'None' : `${m}m`}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] flex items-center gap-1"><Wrench className="h-2.5 w-2.5 text-muted-foreground" /> Breakdown</Label>
                      <Select value={form.breakdown_time} onValueChange={v => set('breakdown_time', v)}>
                        <SelectTrigger className="bg-muted border-border h-8 text-xs" data-testid="breakdown-time-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{['0','15','30','45','60','90','120'].map(m => <SelectItem key={m} value={m}>{m === '0' ? 'None' : `${m}m`}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px]">Your Name *</Label><Input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} className="bg-muted border-border h-8 text-xs" data-testid="checkout-name" /></div>
                  <div><Label className="text-[10px]">Email *</Label><Input type="email" value={form.customer_email} onChange={e => set('customer_email', e.target.value)} className="bg-muted border-border h-8 text-xs" data-testid="checkout-email" /></div>
                </div>
                <div><Label className="text-[10px]">Phone</Label><Input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="bg-muted border-border text-xs" rows={2} placeholder="Special requests..." /></div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Order Summary */}
          <aside className="w-full lg:w-80 flex-shrink-0 mt-4 lg:mt-0 lg:sticky lg:top-20 lg:self-start" data-testid="order-summary">
            <Card className="bg-card/80 border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4 text-emerald-400" /> Order Summary
                </h3>
                {pricing ? (
                  <div className="space-y-2 text-xs">
                    {/* === SPACES === */}
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wide">Venue Spaces</p>
                    {spacesBreakdown ? (
                      <>
                        {spacesBreakdown.map(sb => (
                          <div key={sb.space_id} className="flex justify-between text-muted-foreground">
                            <span className="truncate mr-2">{sb.space_name} <span className="text-muted-foreground">({sb.hours}hrs)</span></span>
                            <span className="text-foreground flex-shrink-0">${sb.cost.toLocaleString()}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-muted-foreground font-medium pt-1 border-t border-border/60">
                          <span>Spaces Subtotal</span>
                          <span className="text-foreground">${pricing.space_cost?.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-muted-foreground">
                        <span>{preview?.space_name || 'Space Rental'}</span>
                        <span className="text-foreground">${pricing.space_cost?.toLocaleString()}</span>
                      </div>
                    )}

                    {/* === ADD-ONS === */}
                    {pricing.addon_items?.length > 0 && (
                      <>
                        <div className="border-t border-border my-1.5" />
                        <p className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wide">Add-ons</p>
                        {pricing.addon_items.map(ai => (
                          <div key={ai.addon_id} className="flex justify-between text-muted-foreground">
                            <span className="truncate mr-2">{ai.name} {ai.quantity > 1 ? <span className="text-muted-foreground">x{ai.quantity}</span> : ''}</span>
                            <span className="text-foreground flex-shrink-0">${ai.total.toLocaleString()}</span>
                          </div>
                        ))}
                      </>
                    )}
                    {pricing.addons_total > 0 && !pricing.addon_items?.length && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Add-ons</span>
                        <span className="text-foreground">${pricing.addons_total?.toLocaleString()}</span>
                      </div>
                    )}

                    {/* Package price override */}
                    {pkg && pricing.venue_subtotal !== pricing.space_cost + pricing.addons_total && (
                      <div className="flex justify-between text-muted-foreground font-medium pt-1 border-t border-border/60">
                        <span>Package Price</span>
                        <span className="text-foreground">${pricing.venue_subtotal?.toLocaleString()}</span>
                      </div>
                    )}

                    {/* === SERVICES by category === */}
                    {pricing.services?.length > 0 && (() => {
                      const grouped = {};
                      pricing.services.forEach(svc => {
                        const cat = svc.category || 'Other';
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(svc);
                      });
                      const CAT_LABELS = { food: 'Food & Catering', drinks: 'Beverage', entertainment: 'Entertainment', staffing: 'Staffing' };
                      return (
                        <>
                          <div className="border-t border-border my-1.5" />
                          {Object.entries(grouped).map(([cat, svcs]) => (
                            <div key={cat} className="space-y-1">
                              <p className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wide">{CAT_LABELS[cat] || cat}</p>
                              {svcs.map(svc => (
                                <div key={svc.service_id} className="flex justify-between text-muted-foreground">
                                  <span className="truncate mr-2">
                                    {svc.name}
                                    {svc.from_package && <span className="text-[8px] text-indigo-400 ml-1">(pkg)</span>}
                                  </span>
                                  <span className="text-foreground flex-shrink-0">${svc.calculated_price?.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </>
                      );
                    })()}

                    {/* Admin Service Fee - only food/drinks */}
                    {pricing.service_markup > 0 && (
                      <>
                        <div className="border-t border-border my-1.5" />
                        <div className="flex justify-between text-muted-foreground">
                          <span className="flex items-center gap-1">
                            Admin Service Fee <span className="text-[8px]">({pricing.service_markup_pct}%)</span>
                          </span>
                          <span className="text-muted-foreground">${pricing.service_markup?.toLocaleString()}</span>
                        </div>
                        <p className="text-[8px] text-muted-foreground -mt-1">Applied to Food & Beverage only</p>
                      </>
                    )}
                    {pricing.fee_eligible_total > 0 && pricing.fee_eligible_total < pricing.services_total && (
                      <p className="text-[8px] text-muted-foreground">Fee on ${pricing.fee_eligible_total?.toLocaleString()} of ${pricing.services_total?.toLocaleString()} in services</p>
                    )}

                    {/* === TOTAL === */}
                    <div className="border-t border-border my-2 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-foreground">Total</span>
                        <span className="text-xl font-bold text-emerald-400" data-testid="grand-total">${pricing.grand_total?.toLocaleString()}</span>
                      </div>
                    </div>
                    {(form.setup_time !== '0' || form.breakdown_time !== '0') && (
                      <div className="pt-1 border-t border-border">
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Wrench className="h-2.5 w-2.5" /> Setup: {form.setup_time}min | Breakdown: {form.breakdown_time}min</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin text-indigo-400 mx-auto" /></div>
                )}
                <Button className="w-full mt-4 bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold" onClick={handleCheckout} disabled={submitting} data-testid="checkout-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CreditCard className="h-4 w-4 mr-1.5" />}
                  Proceed to Payment
                </Button>
                <p className="text-[9px] text-muted-foreground text-center mt-2">Secure checkout powered by Stripe. 15% venue commission included.</p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>

      {/* ===== FULL SERVICE DETAIL MODAL (matches Event Planning Hub) ===== */}
      <Dialog open={!!detailSvc} onOpenChange={() => { setDetailSvc(null); setDetailSvcFull(null); }}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-detail-modal">
          {detailSvc && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg text-foreground">{detailSvc.name}</DialogTitle>
                  {detailSvcFull?.is_featured && <Badge className="bg-amber-500 text-[9px]">Featured</Badge>}
                </div>
                <div className="flex gap-2 mt-1">
                  <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px] capitalize">{detailSvc.category}</Badge>
                  {detailSvc.sub_category && <Badge className="bg-muted text-muted-foreground text-[10px] capitalize">{detailSvc.sub_category}</Badge>}
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">{PRICING_MODEL_LABELS[detailSvc.pricing_model] || detailSvc.pricing_model}</Badge>
                </div>
              </DialogHeader>

              {detailLoading ? (
                <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400 mx-auto" /><p className="text-sm text-muted-foreground mt-2">Loading details...</p></div>
              ) : detailSvcFull ? (
                <>
                  {/* Gallery */}
                  {detailSvcFull.gallery_images?.length > 0 && (
                    <div className="space-y-2">
                      <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                        <img src={detailSvcFull.gallery_images[detailGalleryIdx] || detailSvc.image_url} alt={detailSvc.name} className="w-full h-full object-cover" />
                      </div>
                      {detailSvcFull.gallery_images.length > 1 && (
                        <div className="flex gap-1.5 overflow-x-auto pb-1">
                          {detailSvcFull.gallery_images.map((img, idx) => (
                            <button key={idx} onClick={() => setDetailGalleryIdx(idx)}
                              className={`w-16 h-11 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${detailGalleryIdx === idx ? 'border-indigo-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Drop-off vs Serviced */}
                  {detailSvcFull.has_dropoff_option && (
                    <div className="p-3 rounded-lg border border-border bg-muted/30 mt-2">
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-emerald-400" /> Service Type</p>
                      <RadioGroup value={selectedServiceType} onValueChange={setSelectedServiceType} className="space-y-2">
                        <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all text-sm ${selectedServiceType === 'serviced' ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-border hover:border-border'}`}>
                          <RadioGroupItem value="serviced" />
                          <div className="flex-1">
                            <span className="font-medium text-foreground">Full Service</span>
                            <p className="text-[10px] text-muted-foreground">Staff sets up, serves, and cleans up</p>
                          </div>
                          <span className="text-sm font-bold text-foreground">${detailSvc.base_price}/{detailSvc.pricing_model?.replace('_', ' ') || 'flat'}</span>
                        </label>
                        <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all text-sm ${selectedServiceType === 'dropoff' ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-border hover:border-border'}`}>
                          <RadioGroupItem value="dropoff" />
                          <div className="flex-1">
                            <span className="font-medium text-foreground">Drop-off</span>
                            <p className="text-[10px] text-muted-foreground">Delivered and set up, you handle the rest</p>
                          </div>
                          <span className="text-sm font-bold text-emerald-400">${detailSvcFull.dropoff_price} flat</span>
                        </label>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Tabs */}
                  <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-3">
                    <TabsList className="bg-muted/80 border border-border">
                      <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-muted">Overview</TabsTrigger>
                      <TabsTrigger value="included" className="text-xs data-[state=active]:bg-muted">What's Included</TabsTrigger>
                      {detailSvcFull.option_groups?.length > 0 && (
                        <TabsTrigger value="customize" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-foreground">Customize</TabsTrigger>
                      )}
                    </TabsList>

                    <TabsContent value="overview" className="space-y-3 mt-3">
                      <div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{detailSvcFull.what_to_expect || detailSvcFull.description || detailSvc.short_description}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Base Price</span>
                          <span className="font-bold text-emerald-400">${detailSvc.base_price}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">{PRICING_MODEL_LABELS[detailSvc.pricing_model]}</p>
                        {detailSvcFull.min_guests && <p className="text-[10px] text-muted-foreground">Min: {detailSvcFull.min_guests} guests | Max: {detailSvcFull.max_guests} guests</p>}
                      </div>
                      {detailSvcFull.host_requirements?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Host Requirements</p>
                          <ul className="space-y-1">
                            {detailSvcFull.host_requirements.map((r, i) => <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1.5"><span className="text-amber-400 mt-0.5">-</span>{r}</li>)}
                          </ul>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="included" className="mt-3">
                      {detailSvcFull.whats_included?.length > 0 ? (
                        <ul className="space-y-2">
                          {detailSvcFull.whats_included.map((item, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />{item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">No inclusion details available.</p>
                      )}
                    </TabsContent>

                    {/* Customize Tab */}
                    {detailSvcFull.option_groups?.length > 0 && (
                      <TabsContent value="customize" className="mt-3 space-y-4">
                        {selectedServiceType === 'dropoff' ? (
                          <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">Customization options are only available with Full Service. Switch to Full Service to customize.</p>
                        ) : (
                          detailSvcFull.option_groups.map(og => (
                            <div key={og.option_id} className="p-3 rounded-lg border border-border bg-muted/30">
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                  {og.name}
                                  {og.required && <Badge className="bg-red-500/20 text-red-400 text-[7px]">Required</Badge>}
                                </p>
                                {og.description && <p className="text-[10px] text-muted-foreground">{og.description}</p>}
                              </div>
                              {og.multiple_select ? (
                                <div className="space-y-1.5">
                                  {og.choices?.map(choice => (
                                    <label key={choice.choice_id} className="flex items-center gap-2.5 p-2 rounded-lg border border-border cursor-pointer hover:border-border bg-muted/50 transition-all">
                                      <Checkbox
                                        checked={(selectedOptions[og.option_id] || []).includes(choice.choice_id)}
                                        onCheckedChange={() => handleOptionChange(og.option_id, choice.choice_id, true)}
                                        className="data-[state=checked]:bg-indigo-500"
                                      />
                                      <div className="flex-1">
                                        <span className="text-xs font-medium text-foreground">{choice.name}</span>
                                        {choice.description && <p className="text-[9px] text-muted-foreground">{choice.description}</p>}
                                      </div>
                                      <span className={`text-xs font-medium ${choice.price_modifier > 0 ? 'text-indigo-400' : 'text-muted-foreground'}`}>
                                        {choice.price_modifier > 0 ? `+$${choice.price_modifier.toFixed(2)}` : 'Included'}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <RadioGroup value={selectedOptions[og.option_id] || ''} onValueChange={(v) => handleOptionChange(og.option_id, v, false)}>
                                  <div className="space-y-1.5">
                                    {og.choices?.map(choice => (
                                      <label key={choice.choice_id} className="flex items-center gap-2.5 p-2 rounded-lg border border-border cursor-pointer hover:border-border bg-muted/50 transition-all">
                                        <RadioGroupItem value={choice.choice_id} />
                                        <div className="flex-1">
                                          <span className="text-xs font-medium text-foreground">{choice.name}</span>
                                          {choice.description && <p className="text-[9px] text-muted-foreground">{choice.description}</p>}
                                        </div>
                                        <span className={`text-xs font-medium ${choice.price_modifier !== 0 ? 'text-indigo-400' : 'text-muted-foreground'}`}>
                                          {choice.price_modifier > 0 ? `+$${choice.price_modifier.toFixed(2)}` : choice.price_modifier < 0 ? `-$${Math.abs(choice.price_modifier).toFixed(2)}` : 'Included'}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </RadioGroup>
                              )}
                            </div>
                          ))
                        )}
                      </TabsContent>
                    )}
                  </Tabs>
                </>
              ) : (
                <div className="space-y-3 mt-2">
                  {detailSvc.image_url && <img src={detailSvc.image_url} alt="" className="w-full h-40 object-cover rounded-lg" />}
                  {detailSvc.short_description && <p className="text-sm text-muted-foreground">{detailSvc.short_description}</p>}
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Base Price</span>
                      <span className="font-bold text-emerald-400">${detailSvc.base_price}</span>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => { setDetailSvc(null); setDetailSvcFull(null); }} className="border-border text-muted-foreground">Close</Button>
                <Button
                  className={`flex-1 ${selectedServiceType === 'dropoff' && detailSvcFull?.has_dropoff_option ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-lime-500 hover:bg-lime-600 text-slate-900'}`}
                  onClick={addServiceFromModal}
                  data-testid="modal-add-service-btn">
                  {extraServiceIds.includes(detailSvc.service_id) ? (
                    <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Update & Save</>
                  ) : selectedServiceType === 'dropoff' && detailSvcFull?.has_dropoff_option ? (
                    <><Truck className="h-4 w-4 mr-1.5" /> Add Drop-off to Booking</>
                  ) : (
                    <><ShoppingCart className="h-4 w-4 mr-1.5" /> Add to Booking</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
